import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import IntlService from 'ember-intl/services/intl';
import { calculateReorderQty } from 'volta/models/planning';
import { mod, parseNumber, round } from 'volta/utils/math-utils';

interface IArgs {
  reorderQty: number;
  moq?: number;
  orderMultiple?: number;
  maxPOQty?: number;
  unitMeasure: string;
  disabled: boolean;
  strictMode?: boolean;
  onChange: (value?: number) => void;
  onCommit: (value?: number) => void;
}

export default class ReorderQtyEdit extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare intl: IntlService;

  // Properties
  // ~~~~~

  @tracked model: any;
  @tracked inputValue?: number = this.args.reorderQty;

  originalValue?: number = this.args.reorderQty;
  fieldId = `${guidFor(this)}-ReorderTextField`;

  // Computed Properties
  // ~~~~~

  get isUnit() {
    return this.args.unitMeasure === 'UNIT';
  }

  get isEdited() {
    return this.inputValue !== this.args.reorderQty;
  }

  get canClose() {
    return this.originalValue === this.inputValue || !this.isEdited;
  }

  get calculatedReorderQty() {
    const { reorderQty, orderMultiple = 1, unitMeasure } = this.args;
    const value = !isPresent(this.inputValue)
      ? reorderQty
      : calculateReorderQty(this.inputValue as number, orderMultiple, unitMeasure);
    return this.normalizeQty(value) ?? 0.0;
  }

  get errors() {
    const errors: string[] = [];
    const { intl, inputValue = 0 } = this;
    const { orderMultiple, maxPOQty } = this.args;

    if (inputValue !== 0) {
      if (orderMultiple && (inputValue < orderMultiple || mod(inputValue,orderMultiple) > 0)) {
        errors.push(intl.t('errors.planningOrders.qtyNotMultiple', { orderMultiple }));
      }
      if (maxPOQty && inputValue > maxPOQty) {
        errors.push(intl.t('errors.planningOrders.qtyGtMaxPOQty', { maxPOQty }));
      }
    }

    return errors;
  }

  get canCommit() {
    return this.errors.length === 0 || !this.args.strictMode;
  }

  // Actions
  // ~~~~~

  @action
  handleClose() {
    return this.canClose;
  }

  @action
  handleReorderUpdate(_el: HTMLElement, [reorderQty]: number[]) {
    this.inputValue = reorderQty;
  }

  @action
  onChangeValue(inputValue?: string | number) {
    const normalizedQty = isPresent(inputValue)
      ? this.normalizeQty(parseNumber(inputValue) as number)
      : undefined;
    this.inputValue = normalizedQty;
    return this.args.onChange?.(normalizedQty);
  }

  @action
  increase() {
    const { orderMultiple = 1 } = this.args;
    const om = orderMultiple <= 0 ? 1 : orderMultiple;
    const calcReorderQty = this.calculatedReorderQty;
    const value =
      calcReorderQty < om
        ? om
        : calcReorderQty <= (this.inputValue ?? calcReorderQty)
        ? calcReorderQty + om
        : calcReorderQty;
    this.inputValue = this.normalizeQty(value);
    this.args.onChange?.(this.inputValue);
  }

  @action
  decrease() {
    const { orderMultiple = 1 } = this.args;
    const om = orderMultiple <= 0 ? 1 : orderMultiple;
    const calcReorderQty = this.calculatedReorderQty;
    const value = Math.max(calcReorderQty - om, om);
    this.inputValue = this.normalizeQty(value);
    return this.args.onChange?.(this.inputValue);
  }

  @action
  async onCommitValue() {
    if (this.canCommit) {
      const normalizedQty = isPresent(this.inputValue)
        ? this.normalizeQty(parseNumber(this.inputValue) as number)
        : 0;
      this.originalValue = this.inputValue;
      return await this.args.onCommit?.(normalizedQty);
    }
  }

  @action
  revertChanges() {
    this.inputValue = this.originalValue;
    return this.args.onChange?.(this.inputValue);
  }

  // Helper Functions
  // ~~~~~

  normalizeQty(qty?: number) {
    return round(qty, this.isUnit ? 0 : 3);
  }
}
